import { isTrpcFetchAbortError } from '@risksmart-app/components/src/utils/trpc/classification';
import type { Query } from '@tanstack/react-query';

/**
 * Whether a query failure is just the portal navigating away mid-flight.
 *
 * Navigating away aborts the in-flight fetch, which surfaces as a
 * `TRPCClientError` wrapping a "Failed to fetch"/AbortError. That is a normal
 * lifecycle event, not a failure worth reporting, so the QueryCache reporter
 * drops it when the query has no observers left.
 *
 * The abort classification itself is shared with web — both apps are the same
 * browser → tRPC hop (COI-159). The portal used to keep a byte-identical copy
 * of the three helpers behind `isTrpcFetchAbortError`; what it did NOT copy was
 * the retry policy above them, which is how it ended up retrying every 403
 * three times on react-query's defaults.
 *
 * Web's equivalent (`shouldReportTrpcQueryError`) is deliberately wider: it
 * also suppresses during a document unload and honours a per-query opt-out.
 * Neither has a consumer here yet.
 */
export const isUnmountedTrpcQueryFetchError = (
  error: unknown,
  query: Pick<Query, 'getObserversCount'>
): boolean => query.getObserversCount() === 0 && isTrpcFetchAbortError(error);
